import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import './SowForm.css';

export default function SowForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEdit = !!id;

  const [form, setForm] = useState({
    sow_ref: '', version: 1.0, msa_id: '', customer_id: '', billing_group_id: '',
    sow_start: '', sow_end: '', project_code: '', status: 'Draft',
    provider_signatory_id: '', provider_address_id: '',
    billing_model: 'Time & Material', billing_term: 'Monthly',
    billing_freq: 'Monthly', invoice_gen_date: 1, prorata_billing: 'Yes',
    agreed_currency: 'USD', invoicing_currency: 'USD', currency_conv_clause: 'RBI ref rate',
    national_holidays: '', ot_clause: '1.5x beyond 9hrs', std_working_hours: 180,
    timesheet_approval_days: 5, auto_approval_days: 7, escalation_eff_date: '',
    resource_name: '', designation: '', skill: '', billing_rate: '',
    rfp_num: '', employee_num: '',
    parent_sow_id: null, new_version_effective_from: ''
  });
  const [dynFields, setDynFields] = useState([]);
  const [kfv, setKfv] = useState({});
  const [msaKfv, setMsaKfv] = useState({});
  const [msas, setMsas] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [clauseTitles, setClauseTitles] = useState([]);
  const [selectedClauses, setSelectedClauses] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [provider, setProvider] = useState({});
  const [customer, setCustomer] = useState({});
  const [currencies, setCurrencies] = useState([]);
  const [signatories, setSignatories] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [billingGroups, setBillingGroups] = useState([]);
  const [selectedSig, setSelectedSig] = useState(null);
  const [selectedAddr, setSelectedAddr] = useState(null);
  const [versionModal, setVersionModal] = useState(null);

  useEffect(() => {
    api.get('/msa').then(rows => setMsas(Array.isArray(rows) ? rows : []));
    api.get('/customers').then(rows => setCustomers(Array.isArray(rows) ? rows : []));
    api.get('/settings/currencies').then(c =>
      setCurrencies(Array.isArray(c) ? c.filter(x => x.is_active) : []));
    api.get('/settings/signatories').then(s =>
      setSignatories(Array.isArray(s) ? s.filter(x => x.is_active) : []));
    api.get('/settings/addresses').then(a =>
      setAddresses(Array.isArray(a) ? a.filter(x => x.is_active) : []));
    api.get('/key-fields').then(kf => setDynFields(Array.isArray(kf) ? kf.filter(f => f.is_active && (f.applies_to === 'SOW' || f.applies_to === 'Both')) : []));
    api.get('/clauses/sow/titles').then(titles => {
      const list = Array.isArray(titles) ? titles : [];
      setClauseTitles(list);
      if (!id) {
        setSelectedClauses(list.map(t => ({
          clause_title: t.clause_title, selected_template: (Array.isArray(t.templates) && t.templates[0]?.template_name) || '',
          sort_order: t.sort_order, show: 1
        })));
      }
    }).catch(() => {
      setClauseTitles([]);
      if (!id) setSelectedClauses([]);
    });
    api.get('/settings/provider').then(setProvider);
  }, [id]);

  useEffect(() => {
    if (id) {
      api.get(`/sow/${id}`).then(data => {
        if (!data || typeof data !== 'object') return;
        setForm({
          sow_ref: data.sow_ref || '', version: data.version, msa_id: data.msa_id,
          customer_id: data.customer_id, billing_group_id: data.billing_group_id || '',
          sow_start: data.sow_start || '', sow_end: data.sow_end || '',
          project_code: data.project_code || '', status: data.status,
          provider_signatory_id: data.provider_signatory_id || '',
          provider_address_id: data.provider_address_id || '',
          billing_model: data.billing_model, billing_term: data.billing_term,
          billing_freq: data.billing_freq, invoice_gen_date: data.invoice_gen_date,
          prorata_billing: data.prorata_billing, agreed_currency: data.agreed_currency,
          invoicing_currency: data.invoicing_currency, currency_conv_clause: data.currency_conv_clause,
          national_holidays: data.national_holidays || '', ot_clause: data.ot_clause || '',
          std_working_hours: data.std_working_hours, timesheet_approval_days: data.timesheet_approval_days,
          auto_approval_days: data.auto_approval_days, escalation_eff_date: data.escalation_eff_date || '',
          resource_name: data.resource_name || '', designation: data.designation || '',
          skill: data.skill || '', billing_rate: data.billing_rate || '',
          rfp_num: data.rfp_num || '', employee_num: data.employee_num || '',
          parent_sow_id: data.parent_sow_id || null, new_version_effective_from: data.new_version_effective_from || ''
        });
        if (data.key_field_values && typeof data.key_field_values === 'object') setKfv(data.key_field_values);
        if (data.msa_key_field_values && typeof data.msa_key_field_values === 'object') setMsaKfv(data.msa_key_field_values);
        if (Array.isArray(data.clauses) && data.clauses.length) setSelectedClauses(data.clauses);
        if (data.provider) setProvider(data.provider);
        setCustomer({ legal_name: data.client_name, registered_address: data.client_address, country: data.client_country, auth_signatory: data.client_signatory, signatory_title: data.client_title });
        if (data.provider_signatory_name) setSelectedSig({ name: data.provider_signatory_name, title: data.provider_signatory_title });
        if (data.provider_address_title) setSelectedAddr({ address_title: data.provider_address_title, address_line: data.provider_address_line, city: data.provider_city });
      }).catch(() => {});
    }
  }, [id]);

  // Load billing groups + parent MSA key-field values (for inheritance)
  useEffect(() => {
    if (form.msa_id) {
      api.get(`/msa/${form.msa_id}/billing-groups`).then(rows =>
        setBillingGroups(Array.isArray(rows) ? rows : [])).catch(() => setBillingGroups([]));
      const m = msas.find(x => x.msa_id === form.msa_id);
      if (m && !isEdit) setForm(f => ({ ...f, customer_id: m.customer_id }));
      if (!isEdit) {
        api.get(`/msa/${form.msa_id}`).then(data => {
          if (data.key_field_values) setMsaKfv(data.key_field_values);
        }).catch(() => {});
      }
    }
  }, [form.msa_id, msas, isEdit]);

  useEffect(() => {
    if (form.customer_id) {
      const c = customers.find(x => x.customer_id === form.customer_id);
      if (c) setCustomer(c);
    }
  }, [form.customer_id, customers]);

  useEffect(() => {
    if (form.provider_signatory_id) {
      const s = signatories.find(x => x.id === parseInt(form.provider_signatory_id));
      if (s) setSelectedSig(s);
    }
  }, [form.provider_signatory_id, signatories]);

  useEffect(() => {
    if (form.provider_address_id) {
      const a = addresses.find(x => x.id === parseInt(form.provider_address_id));
      if (a) setSelectedAddr(a);
    }
  }, [form.provider_address_id, addresses]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const updateClause = (idx, field, value) => setSelectedClauses(prev => (Array.isArray(prev) ? prev : []).map((c, i) => i === idx ? { ...c, [field]: value } : c));

  const substituteVars = (text) => {
    if (!text) return '';
    const vars = {
      ...msaKfv, ...kfv,
      provider_name: provider.provider_name || '', client_name: customer.legal_name || '',
      billing_term: form.billing_term || '', billing_frequency: form.billing_freq || '',
      invoice_gen_date: form.invoice_gen_date || '', invoicing_currency: form.invoicing_currency || '',
      prorata_billing: form.prorata_billing || '', agreed_currency: form.agreed_currency || '',
      currency_conv_clause: form.currency_conv_clause || '', std_working_hours: form.std_working_hours || '',
      national_holidays: form.national_holidays || '', ot_clause: form.ot_clause || '',
      timesheet_approval_days: form.timesheet_approval_days || '', auto_approval_days: form.auto_approval_days || '',
      escalation_eff_date: form.escalation_eff_date || '',
    };
    return text.replace(/\{\{(\w+)\}\}/g, (_, key) => vars[key] !== undefined ? vars[key] : `{{${key}}}`);
  };

  const previewClauses = useMemo(() => {
    const rows = Array.isArray(selectedClauses) ? selectedClauses : [];
    const titles = Array.isArray(clauseTitles) ? clauseTitles : [];
    return rows.filter(c => c.show).sort((a, b) => a.sort_order - b.sort_order).map(c => {
      const ct = titles.find(x => x.clause_title === c.clause_title);
      const tpls = ct && Array.isArray(ct.templates) ? ct.templates : [];
      const tpl = tpls.find(t => t.template_name === c.selected_template);
      return { title: c.clause_title, text: tpl ? substituteVars(tpl.template_text) : 'Template not found' };
    });
  }, [selectedClauses, clauseTitles, form, provider, customer, kfv, msaKfv]);

  const groupedDynFieldEntries = useMemo(() => {
    if (!Array.isArray(dynFields) || dynFields.length === 0) return [];
    const acc = {};
    for (const f of dynFields) {
      if (!f || typeof f !== 'object') continue;
      const g = f.field_group || 'Other';
      if (!acc[g]) acc[g] = [];
      acc[g].push(f);
    }
    return Object.entries(acc);
  }, [dynFields]);

  const saveSow = async () => {
    const payload = { ...form, clauses: selectedClauses, key_field_values: kfv };
    if (isEdit) await api.put(`/sow/${id}`, payload);
    else await api.post('/sow', payload);
  };

  const openNewVersion = () => setVersionModal({
    version_comments: '', new_version_effective_from: '',
    created_on: new Date().toISOString().slice(0, 10)
  });

  const submitNewVersion = async () => {
    if (!versionModal.version_comments.trim()) { alert('Version Comments are required.'); return; }
    try {
      const r = await api.post(`/sow/${id}/new-version`, {
        new_version_effective_from: versionModal.new_version_effective_from || null,
        version_comments: versionModal.version_comments
      });
      alert(`Created ${r.sow_id} (v${r.version}) as Draft.`);
      setVersionModal(null);
      navigate(`/sow/${r.sow_id}`);
    } catch (e) { alert(e.message); }
  };

  const handleUnconfirm = async () => {
    if (!window.confirm('Mark this SOW as Unconfirmed?')) return;
    try { await api.post(`/sow/${id}/unconfirm`, {}); alert('Marked Unconfirmed.'); navigate('/sow'); }
    catch (e) { alert(e.message); }
  };

  const handleSave = async () => {
    try {
      await saveSow();
      navigate('/sow');
    } catch (e) { alert(e.message); }
  };

  const handleSubmit = async () => {
    if (!window.confirm('Submit this SOW for approval?')) return;
    try {
      await saveSow();
      await api.post('/approvals/submit', { entity_type: 'SOW', entity_id: id });
      navigate('/sow');
    } catch (e) { alert(e.message); }
  };

  const isLocked = form.status === 'Submitted' || form.status === 'Approved' || form.status === 'Active';

  const displayTitle = isEdit ? (form.sow_ref?.trim() ? form.sow_ref.trim() : id) : 'New SOW';
  const introSub = isEdit
    ? [form.resource_name, customer.legal_name, form.msa_id].filter(Boolean).join(' · ') || 'Statement of Work'
    : 'Statement of Work under an approved or active MSA — clauses, billing, and live preview.';

  const canSaveSow = !isLocked;
  const canSubmitApproval = isEdit && (form.status === 'Draft' || form.status === 'Rejected');
  const canCreateNewVersion =
    isEdit && ['Active', 'Approved', 'Expired', 'Terminated'].includes(form.status) && user?.role === 'Admin';
  const canMarkUnconfirm = isEdit && user?.role === 'Admin' && form.status !== 'Unconfirmed';
  const showSowToolbarActions = canSaveSow || canSubmitApproval || canCreateNewVersion || canMarkUnconfirm;

  return (
    <>
      <div className="page-header">
        <h1>SOW Agreements</h1>
      </div>
      <div className="page-content sow-form-page">
        <div className="sow-form-toolbar">
          <div className="sow-form-toolbar-top">
            <nav className="page-back-nav" aria-label="Back navigation">
              <Link to="/sow" className="page-back-link">
                ← Back to SOW agreements
              </Link>
            </nav>
            {showSowToolbarActions && (
              <div className="sow-form-toolbar-actions" role="group" aria-label="SOW actions">
                {canSaveSow && (
                  <button type="button" className="btn btn-primary" onClick={handleSave}>
                    Save SOW
                  </button>
                )}
                {canSubmitApproval && (
                  <button type="button" className="btn btn-primary" onClick={handleSubmit}>
                    Submit for Approval
                  </button>
                )}
                {canCreateNewVersion && (
                  <button type="button" className="btn btn-secondary" onClick={openNewVersion}>
                    + Create New Version
                  </button>
                )}
                {canMarkUnconfirm && (
                  <button type="button" className="btn btn-outline" onClick={handleUnconfirm}>
                    Mark Unconfirmed
                  </button>
                )}
              </div>
            )}
          </div>

          <header className="sow-form-intro">
            <h2 className="sow-form-intro-title">{displayTitle}</h2>
            <p className="sow-form-intro-sub">{introSub}</p>
          </header>
        </div>

        {isEdit ? (
          <section className="sow-form-glance" aria-labelledby="sow-glance-heading">
            <h2 id="sow-glance-heading" className="sow-form-glance-title">
              At a glance
            </h2>
            <div className="sow-form-summary" aria-label="SOW summary">
            <div className="sow-form-summary-card">
              <div className="sow-form-summary-card-label">Status</div>
              <div className="sow-form-summary-card-value">
                <span className={`badge badge-${form.status?.toLowerCase()}`}>{form.status}</span>
              </div>
            </div>
            <div className="sow-form-summary-card">
              <div className="sow-form-summary-card-label">Version</div>
              <div className="sow-form-summary-card-value tabular-nums">{form.version}</div>
            </div>
            <div className="sow-form-summary-card">
              <div className="sow-form-summary-card-label">SOW ID</div>
              <div className="sow-form-summary-card-value tabular-nums sow-form-summary-card-value-wrap">{id}</div>
            </div>
            {form.sow_ref?.trim() && form.sow_ref.trim() !== id && (
              <div className="sow-form-summary-card">
                <div className="sow-form-summary-card-label">Reference</div>
                <div className="sow-form-summary-card-value sow-form-summary-card-value-wrap">{form.sow_ref.trim()}</div>
              </div>
            )}
            <div className="sow-form-summary-card">
              <div className="sow-form-summary-card-label">Customer</div>
              <div className={`sow-form-summary-card-value ${(customer.legal_name || '').length > 32 ? 'sow-form-summary-card-value-wrap' : ''}`}>
                {customer.legal_name || '—'}
              </div>
            </div>
            <div className="sow-form-summary-card">
              <div className="sow-form-summary-card-label">MSA</div>
              <div className="sow-form-summary-card-value" title={form.msa_id}>
                {form.msa_id || '—'}
              </div>
            </div>
            <div className="sow-form-summary-card">
              <div className="sow-form-summary-card-label">Period</div>
              <div className="sow-form-summary-card-value sow-form-summary-card-value-wrap">
                {form.sow_start || form.sow_end ? `${form.sow_start || '—'} → ${form.sow_end || '—'}` : '—'}
              </div>
            </div>
            <div className="sow-form-summary-card">
              <div className="sow-form-summary-card-label">Engagement</div>
              <div className="sow-form-summary-card-value sow-form-summary-card-value-wrap">
                {form.billing_model || '—'}
                {form.resource_name ? ` · ${form.resource_name}` : ''}
              </div>
            </div>
            {isLocked && (
              <div className="sow-form-summary-card">
                <div className="sow-form-summary-card-label">Edits</div>
                <div className="sow-form-summary-card-value">
                  <span className="badge badge-submitted">Read-only</span>
                </div>
              </div>
            )}
            </div>
          </section>
        ) : (
          <p className="sow-form-new-hint">
            Select an <strong>MSA</strong>, confirm billing and resource details, then configure SOW clauses. Use <strong>Prepare Agreement</strong> for a live preview with variables substituted from this form and the parent MSA.
          </p>
        )}

        <div className="split-panel sow-form-split">
          <div className="left-panel">
            {/* Agreement Controls - FIRST */}
            <div className="card">
              <div className="card-header">Agreement Controls</div>
              <div className="sow-form-agreement-controls btn-group">
                <button
                  type="button"
                  className={`btn ${showPreview ? 'btn-success' : 'btn-primary'}`}
                  onClick={() => setShowPreview(!showPreview)}
                >
                  {showPreview ? 'Preview Active' : 'Prepare Agreement'}
                </button>
              </div>
            </div>

            <div className="card">
              <div className="card-header">SOW Identification</div>
              <div className="form-grid">
                {isEdit && <div className="form-group"><label>SOW Number</label><input value={id} readOnly /></div>}
                <div className="form-group"><label>Reference Number</label><input value={form.sow_ref} onChange={e => set('sow_ref', e.target.value)} disabled={isLocked} /></div>
                <div className="form-group"><label>Version</label><input value={form.version} readOnly disabled /></div>
                <div className="form-group"><label>MSA *</label>
                  <select value={form.msa_id} onChange={e => set('msa_id', e.target.value)} disabled={isLocked}>
                    <option value="">Select MSA</option>
                    {msas.filter(m => m.status === 'Approved' || m.status === 'Active').map(m => <option key={m.msa_id} value={m.msa_id}>{m.msa_id} - {m.client_name}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>Customer</label><input value={form.customer_id ? `${form.customer_id} - ${customer.legal_name || ''}` : ''} readOnly /></div>
                {billingGroups.length > 0 && (
                  <div className="form-group"><label>Billing Group</label>
                    <select value={form.billing_group_id || ''} onChange={e => set('billing_group_id', e.target.value || null)} disabled={isLocked}>
                      <option value="">-- None --</option>
                      {billingGroups.map(bg => <option key={bg.id} value={bg.id}>{bg.group_name}</option>)}
                    </select>
                  </div>
                )}
                <div className="form-group"><label>SOW Start</label><input type="date" value={form.sow_start} onChange={e => set('sow_start', e.target.value)} disabled={isLocked} /></div>
                <div className="form-group"><label>SOW End</label><input type="date" value={form.sow_end} onChange={e => set('sow_end', e.target.value)} disabled={isLocked} /></div>
                <div className="form-group"><label>Project Code</label><input value={form.project_code} onChange={e => set('project_code', e.target.value)} disabled={isLocked} /></div>
                {form.parent_sow_id && (
                  <>
                    <div className="form-group"><label>Parent SOW</label><input value={form.parent_sow_id} readOnly /></div>
                    <div className="form-group"><label>New-Version Effective From</label><input type="date" value={form.new_version_effective_from} onChange={e => set('new_version_effective_from', e.target.value)} disabled={isLocked} /></div>
                  </>
                )}
              </div>
            </div>

            {/* Provider */}
            <div className="card">
              <div className="card-header">Service Provider</div>
              <div className="form-grid">
                <div className="form-group"><label>Company</label><input value={provider.provider_name || ''} readOnly /></div>
                <div className="form-group"><label>Signatory</label>
                  <select value={form.provider_signatory_id} onChange={e => set('provider_signatory_id', e.target.value)} disabled={isLocked}>
                    <option value="">Select Signatory</option>
                    {signatories.map(s => <option key={s.id} value={s.id}>{s.name} - {s.title}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>Address</label>
                  <select value={form.provider_address_id} onChange={e => set('provider_address_id', e.target.value)} disabled={isLocked}>
                    <option value="">Select Address</option>
                    {addresses.map(a => <option key={a.id} value={a.id}>{a.address_title} - {a.city}</option>)}
                  </select>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-header">Resource (1 per SOW)</div>
              <div className="form-grid">
                <div className="form-group"><label>RFP Number</label><input value={form.rfp_num} onChange={e => set('rfp_num', e.target.value)} disabled={isLocked} /></div>
                <div className="form-group"><label>Employee Number</label><input value={form.employee_num} onChange={e => set('employee_num', e.target.value)} disabled={isLocked} /></div>
                <div className="form-group"><label>Resource Name</label><input value={form.resource_name} onChange={e => set('resource_name', e.target.value)} disabled={isLocked} /></div>
                <div className="form-group"><label>Designation</label><input value={form.designation} onChange={e => set('designation', e.target.value)} disabled={isLocked} /></div>
                <div className="form-group"><label>Skill</label><input value={form.skill} onChange={e => set('skill', e.target.value)} disabled={isLocked} /></div>
                <div className="form-group"><label>Billing Rate</label><input type="number" value={form.billing_rate} onChange={e => set('billing_rate', e.target.value)} disabled={isLocked} /></div>
              </div>
            </div>

            <div className="card">
              <div className="card-header">Billing Configuration</div>
              <div className="form-grid">
                <div className="form-group"><label>Billing Model</label>
                  <select value={form.billing_model} onChange={e => set('billing_model', e.target.value)} disabled={isLocked}>
                    <option>Time & Material</option><option>Fixed Bid</option><option>Monthly-fixed</option><option>Retainer</option>
                  </select>
                </div>
                <div className="form-group"><label>Billing Term</label>
                  <select value={form.billing_term} onChange={e => set('billing_term', e.target.value)} disabled={isLocked}>
                    <option>Monthly</option><option>Hourly</option><option>Monthly-fixed</option><option>Milestone</option>
                  </select>
                </div>
                <div className="form-group"><label>Billing Frequency</label>
                  <select value={form.billing_freq} onChange={e => set('billing_freq', e.target.value)} disabled={isLocked}>
                    <option>Monthly</option><option>Bi-Weekly</option><option>Quarterly</option>
                  </select>
                </div>
                <div className="form-group"><label>Invoice Gen Day</label><input type="number" value={form.invoice_gen_date} onChange={e => set('invoice_gen_date', e.target.value)} disabled={isLocked} /></div>
                <div className="form-group"><label>Pro-rata Billing</label>
                  <select value={form.prorata_billing} onChange={e => set('prorata_billing', e.target.value)} disabled={isLocked}>
                    <option>Yes</option><option>No</option>
                  </select>
                </div>
                <div className="form-group"><label>Agreed Currency</label>
                  <select value={form.agreed_currency} onChange={e => set('agreed_currency', e.target.value)} disabled={isLocked}>
                    {currencies.map(c => <option key={c.code} value={c.code}>{c.code}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>Invoicing Currency</label>
                  <select value={form.invoicing_currency} onChange={e => set('invoicing_currency', e.target.value)} disabled={isLocked}>
                    {currencies.map(c => <option key={c.code} value={c.code}>{c.code}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>Currency Conv. Clause</label><input value={form.currency_conv_clause} onChange={e => set('currency_conv_clause', e.target.value)} disabled={isLocked} /></div>
                <div className="form-group"><label>Std Working Hours/month</label><input type="number" value={form.std_working_hours} onChange={e => set('std_working_hours', e.target.value)} disabled={isLocked} /></div>
                <div className="form-group"><label>National Holidays</label><input value={form.national_holidays} onChange={e => set('national_holidays', e.target.value)} disabled={isLocked} /></div>
                <div className="form-group"><label>OT Clause</label><input value={form.ot_clause} onChange={e => set('ot_clause', e.target.value)} disabled={isLocked} /></div>
                <div className="form-group"><label>Timesheet Approval (days)</label><input type="number" value={form.timesheet_approval_days} onChange={e => set('timesheet_approval_days', e.target.value)} disabled={isLocked} /></div>
                <div className="form-group"><label>Auto-Approval (days)</label><input type="number" value={form.auto_approval_days} onChange={e => set('auto_approval_days', e.target.value)} disabled={isLocked} /></div>
                <div className="form-group"><label>Escalation Eff. Date</label><input type="date" value={form.escalation_eff_date} onChange={e => set('escalation_eff_date', e.target.value)} disabled={isLocked} /></div>
              </div>
            </div>

            <div className="card">
              <div className="card-header">SOW Clause Template Selection</div>
              <table className="clause-table">
                <thead><tr><th>Clause</th><th>Template</th><th>Sort</th><th>Show</th></tr></thead>
                <tbody>
                  {(Array.isArray(selectedClauses) ? selectedClauses : []).map((c, i) => {
                    const titles = Array.isArray(clauseTitles) ? clauseTitles : [];
                    const ct = titles.find(x => x.clause_title === c.clause_title);
                    const isMandatory = ct?.is_mandatory;
                    return (
                      <tr key={i}>
                        <td className="sow-form-clause-title">
                          {c.clause_title}
                          {isMandatory ? <span className="badge badge-approved sow-form-mandatory-mark">M</span> : null}
                        </td>
                        <td>
                          <select value={c.selected_template} onChange={e => updateClause(i, 'selected_template', e.target.value)} disabled={isLocked}>
                            {(Array.isArray(ct?.templates) ? ct.templates : []).map(t => <option key={t.template_name} value={t.template_name}>{t.template_name}</option>)}
                          </select>
                        </td>
                        <td>
                          <input
                            type="number"
                            className="sow-form-sort-input"
                            value={c.sort_order}
                            onChange={e => updateClause(i, 'sort_order', parseInt(e.target.value, 10))}
                            disabled={isLocked}
                          />
                        </td>
                        <td className="text-center">
                          <input type="checkbox" checked={!!c.show} onChange={e => updateClause(i, 'show', e.target.checked ? 1 : 0)} disabled={isLocked || isMandatory} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Other Details (from Key Fields master, with MSA inheritance) */}
            {groupedDynFieldEntries.length > 0 && (
              <div className="card">
                <div className="card-header">Other Details</div>
                {groupedDynFieldEntries.map(([group, fields]) => (
                  <div key={group} className="sow-form-key-field-group-wrap">
                    <h3 className="sow-form-key-field-group-title">{group}</h3>
                    <div className="form-grid">
                      {fields.map(f => {
                        const inheritedFromMsa = msaKfv[f.field_name] !== undefined && msaKfv[f.field_name] !== '';
                        const current = kfv[f.field_name] !== undefined ? kfv[f.field_name] : (inheritedFromMsa ? msaKfv[f.field_name] : '');
                        return (
                          <div key={f.id} className={f.field_type === 'textarea' ? 'form-group full-width' : 'form-group'}>
                            <label>
                              {f.field_label}
                              {inheritedFromMsa && <span className="badge badge-pending sow-form-inherit-badge">from MSA</span>}
                            </label>
                            {f.field_type === 'textarea'
                              ? <textarea value={current} onChange={e => setKfv(p => ({ ...p, [f.field_name]: e.target.value }))} disabled={isLocked} />
                              : <input type={f.field_type === 'number' ? 'number' : f.field_type === 'date' ? 'date' : 'text'} value={current} onChange={e => setKfv(p => ({ ...p, [f.field_name]: e.target.value }))} disabled={isLocked} />}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
                <p className="sow-form-key-field-footnote">
                  Values tagged <strong>from MSA</strong> are inherited from the parent MSA — override here if needed.
                </p>
              </div>
            )}
          </div>

          <div className="sow-form-preview-rail">
            {showPreview ? (
              <div className="agreement-preview">
                <div className="sow-form-preview-logo">
                  <img src="/logo.png" alt="Colan" />
                </div>
                <h1>STATEMENT OF WORK</h1>
                <p className="agreement-subtitle">
                  SOW: {isEdit ? id : '(New)'} | MSA: {form.msa_id} | Client: {customer.legal_name || form.customer_id}
                </p>

                <div className="sow-form-resource-summary">
                  <strong>Resource:</strong> {form.resource_name || '—'} | <strong>Designation:</strong> {form.designation || '—'}
                  <br />
                  <strong>Skill:</strong> {form.skill || '—'} | <strong>Rate:</strong> {form.agreed_currency}{' '}
                  {form.billing_rate != null ? Number(form.billing_rate).toLocaleString() : '—'}
                  <br />
                  <strong>Period:</strong> {form.sow_start || '—'} to {form.sow_end || '—'} | <strong>Project:</strong> {form.project_code || '—'}
                </div>

                {previewClauses.map((c, i) => (
                  <div key={i}>
                    <h3>{i + 1}. {c.title}</h3>
                    <p>{c.text}</p>
                  </div>
                ))}

                <div className="signature-block">
                  <div className="sig-party">
                    <strong>For: {provider.provider_name}</strong>
                    <div>Name: {selectedSig?.name || '___'}</div>
                    <div>Title: {selectedSig?.title || '___'}</div>
                    <div className="sig-line"></div>
                  </div>
                  <div className="sig-party">
                    <strong>For: {customer.legal_name}</strong>
                    <div>Name: {customer.auth_signatory}</div>
                    <div>Title: {customer.signatory_title}</div>
                    <div className="sig-line"></div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="agreement-preview">
                <div className="agreement-placeholder">
                  <h3>Live SOW Preview</h3>
                  <p>Click &quot;Prepare Agreement&quot; to generate the SOW preview.</p>
                  <p className="sow-form-placeholder-note">
                    Clauses and billing fields update the preview as you type once preview is enabled.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {versionModal && (
        <div className="modal-overlay" onClick={() => setVersionModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>Create New SOW Version</h2>
            <p className="sow-form-modal-hint">
              A new draft will be created with values copied from <strong>{id}</strong>. The new version number is auto-assigned (max version in this SOW&apos;s lineage + 1).
            </p>
            <div className="form-grid">
              <div className="form-group full-width"><label>Version Comments *</label>
                <textarea rows={3} value={versionModal.version_comments} onChange={e => setVersionModal(m => ({ ...m, version_comments: e.target.value }))} placeholder="Reason for new version" />
              </div>
              <div className="form-group"><label>Created On</label><input value={versionModal.created_on} readOnly disabled /></div>
              <div className="form-group"><label>New-Version Effective From</label><input type="date" value={versionModal.new_version_effective_from} onChange={e => setVersionModal(m => ({ ...m, new_version_effective_from: e.target.value }))} /></div>
            </div>
            <div className="btn-group mt-4">
              <button className="btn btn-primary" onClick={submitNewVersion}>Create New Version</button>
              <button className="btn btn-secondary" onClick={() => setVersionModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
